import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Chapter, FadeUp } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  objet: "",
  prenom: "",
  nom: "",
  email: "",
  telephone: "",
  cabinet: "",
  fonction: "",
  siret: "",
  code_postal: "",
  contexte: "",
};

const FONCTIONS = ["Avocat", "Mandataire/Administrateur", "Dirigeant", "Conseil financier", "Autre"];

const inputCls =
  "w-full bg-white border-b border-gray-300 focus:border-black outline-none py-3 text-slate-900 placeholder:text-gray-400 transition-colors duration-300 text-base";
const labelCls = "block font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-1";

export default function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setTimeout(() => {
        setLoading(false);
        setSubmitted(true);
      }, 1500);
    } catch (err) {
      setLoading(false);
      toast.error("Une erreur est survenue. Merci de réessayer.");
    }
  };

  return (
    <section id="contact" className="bg-slate-50 border-t border-gray-200" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 sm:py-28 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <FadeUp className="lg:col-span-5">
            <Chapter index="06" label="Contact" />
            <h2 className="mt-8 font-sans font-extrabold text-slate-900 text-3xl sm:text-4xl leading-[1.15] tracking-tight">
              Contact &amp; Création de dossier.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-gray-600 leading-relaxed max-w-md">
              Confiez-nous vos pièces en toute confidentialité. Pour vous permettre de tester
              notre réactivité et notre rigueur, votre premier Mémo Flash d'évaluation vous est
              offert.
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="contact-success-panel"
                className="bg-slate-900 text-white rounded-2xl p-10 sm:p-14"
              >
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
                  ACS Corporate — Confirmation
                </p>
                <p className="mt-6 text-xl sm:text-2xl font-medium leading-relaxed">
                  Demande d'ouverture de dossier enregistrée. Nos équipes vous recontactent sous
                  24h sur une ligne sécurisée.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm">
                <div className="sm:col-span-2">
                  <label htmlFor="objet" className={labelCls}>Objet de la demande</label>
                  <select id="objet" required data-testid="select-objet" className={`${inputCls} cursor-pointer`} value={form.objet} onChange={set("objet")}>
                    <option value="" disabled>Sélectionner</option>
                    <option value="Demande d'informations">Demande d'informations</option>
                    <option value="Ouverture d'un dossier">Ouverture d'un dossier</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="prenom" className={labelCls}>Prénom</label>
                  <input id="prenom" required data-testid="input-prenom" className={inputCls} value={form.prenom} onChange={set("prenom")} placeholder="Jean" />
                </div>
                <div>
                  <label htmlFor="nom" className={labelCls}>Nom</label>
                  <input id="nom" required data-testid="input-nom" className={inputCls} value={form.nom} onChange={set("nom")} placeholder="Dupont" />
                </div>
                <div>
                  <label htmlFor="email" className={labelCls}>Email pro</label>
                  <input id="email" type="email" required data-testid="input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jean.dupont@cabinet.fr" />
                </div>
                <div>
                  <label htmlFor="telephone" className={labelCls}>Téléphone</label>
                  <input id="telephone" type="tel" required data-testid="input-phone" className={inputCls} value={form.telephone} onChange={set("telephone")} placeholder="+33 6 00 00 00 00" />
                </div>
                <div>
                  <label htmlFor="cabinet" className={labelCls}>Nom du cabinet / Entreprise</label>
                  <input id="cabinet" required data-testid="input-cabinet" className={inputCls} value={form.cabinet} onChange={set("cabinet")} placeholder="Cabinet Dupont & Associés" />
                </div>
                <div>
                  <label htmlFor="fonction" className={labelCls}>Fonction</label>
                  <select id="fonction" required data-testid="select-fonction" className={`${inputCls} cursor-pointer`} value={form.fonction} onChange={set("fonction")}>
                    <option value="" disabled>Sélectionner</option>
                    {FONCTIONS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="siret" className={labelCls}>Siret</label>
                  <input id="siret" required data-testid="input-siret" className={inputCls} value={form.siret} onChange={set("siret")} placeholder="000 000 000 00000" />
                </div>
                <div>
                  <label htmlFor="code_postal" className={labelCls}>Code Postal</label>
                  <input id="code_postal" required data-testid="input-code-postal" className={inputCls} value={form.code_postal} onChange={set("code_postal")} placeholder="75008" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contexte" className={labelCls}>Contexte de la demande</label>
                  <textarea id="contexte" required rows={4} data-testid="input-contexte" className={`${inputCls} resize-none`} value={form.contexte} onChange={set("contexte")} placeholder="Décrivez brièvement la situation et les enjeux du dossier…" />
                </div>
                <div className="sm:col-span-2 mt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-contact-button"
                    className="group w-full inline-flex items-center justify-center gap-3 bg-slate-900 text-white px-8 py-5 rounded-full font-medium text-base hover:bg-slate-700 transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading ? "Traitement en cours..." : "Envoyer ma demande sécurisée"}
                    {!loading && <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
                  </button>
                  <p className="mt-5 text-xs text-gray-500 leading-relaxed" data-testid="contact-confidentiality-note">
                    Les informations saisies sont couvertes par le secret professionnel et ne seront
                    jamais partagées.
                  </p>
                </div>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
